// "Sign in with Google" button. Triggers a full-page navigation to the
// backend `/api/auth/google/sign-in/start` route, which 302s to Google.
//
// Branding follows Google's official sign-in button guidelines
// (https://developers.google.com/identity/branding-guidelines): the
// 4-color G mark in its officially specified geometry, "Sign in with
// Google" label in Roboto/system sans, white background with a 1px
// border. Color tokens come from globals.css; the SVG is inline so no
// network round-trip is required.

import type { CSSProperties } from "react";

interface Props {
  /**
   * Where to send the user after a successful login. Defaults to the
   * current pathname so a deep link survives the round-trip. The backend
   * validates this is a same-origin path before redirecting.
   */
  returnTo?: string;
  /** Optional inline style override (e.g. width:auto in narrow contexts). */
  style?: CSSProperties;
  disabled?: boolean;
}

const buttonStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 12,
  width: "100%",
  minHeight: 44,
  padding: "10px 18px",
  background: "#ffffff",
  color: "#1f1f1f",
  border: "1px solid var(--line)",
  borderRadius: "var(--r-pill)",
  fontFamily: "var(--font-ui)",
  fontSize: 16,
  fontWeight: 500,
  cursor: "pointer",
  boxShadow: "var(--shadow-1)",
  transition: "background 120ms ease, box-shadow 120ms ease",
};

const disabledStyle: CSSProperties = {
  opacity: 0.6,
  cursor: "not-allowed",
};

/** Google G mark — official 4-color SVG, 18×18 baseline. */
function GoogleGLogo() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
      />
      <path
        fill="#FF3D00"
        d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
      />
    </svg>
  );
}

export function GoogleSignInButton({ returnTo, style, disabled }: Props) {
  function handleClick() {
    if (disabled) return;
    const fallback =
      typeof window !== "undefined" ? window.location.pathname + window.location.search : "/";
    const target = returnTo ?? fallback;
    // Full-page navigation — a fetch() can't follow Google's 302.
    window.location.assign(
      `/api/auth/google/sign-in/start?return_to=${encodeURIComponent(target)}`,
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      style={{ ...buttonStyle, ...(disabled ? disabledStyle : null), ...style }}
      aria-label="Sign in with Google"
    >
      <GoogleGLogo />
      <span>Sign in with Google</span>
    </button>
  );
}
